import { FC, createContext, useContext, useRef, useEffect, ReactNode } from 'react'
import { isAxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@/providers/SnackbarProvider'
import { ApiService } from '@/services/api'
import { AUTH_QUERY_KEYS } from '@/hooks/queries/query/auth'

interface IApiContext {
  api: ApiService
}

const ApiContext = createContext<IApiContext | null>(null)

export function useApi() {
  const context = useContext(ApiContext)
  if (!context) throw new Error('useApi must be used within an ApiProvider')
  return context
}

interface ApiProviderProps {
  children: ReactNode
}

export const ApiProvider: FC<ApiProviderProps> = ({ children }) => {
  const { showSuccess, showError, showWarning } = useSnackbar()
  const queryClient = useQueryClient()
  const apiRef = useRef<ApiService | null>(null)
  if (!apiRef.current) apiRef.current = new ApiService()
  const api = apiRef.current

  useEffect(() => {
    const id = api._api.interceptors.response.use(
      (res) => res,
      (error: unknown) => {
        if (!isAxiosError(error)) return Promise.reject(error)
        const status = error.response?.status ?? 0

        if (status === 401) {
          queryClient.removeQueries({ queryKey: AUTH_QUERY_KEYS.me })
          const pathname = window.location.pathname
          if (pathname !== '/login' && pathname !== '/auth/callback') {
            void fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
            window.location.href = '/login'
          }
          return Promise.reject(error)
        }

        const message = error.response?.data?.message
        if (message) {
          if (status < 400) showSuccess(message)
          else if (status < 500) showError(message)
          else showWarning(message)
        }
        return Promise.reject(error)
      },
    )
    return () => api._api.interceptors.response.eject(id)
  }, [api, queryClient, showSuccess, showError, showWarning])

  return (
    <ApiContext.Provider value={{ api }}>
      {children}
    </ApiContext.Provider>
  )
}
